import { gql, useQuery } from '@apollo/client';

/**
 * The site owner and the billing email (apiserver platform/services/siteOwner.js).
 * The owner is the one admin who manages billing (plan, card, billing email)
 * and can hand the site to another admin; while a site has no owner, any
 * admin can. Kept out of the screens so the wording can be tested without
 * the router.
 */

export interface BillingContact {
  accountId: string;
  ownerUserId: string | null;
  ownerEmail: string | null;
  billingEmail: string | null;
  effectiveBillingEmail: string | null;
  isOwner: boolean;
  canManage: boolean;
  billingEmailNotOnSite: boolean;
  cardAddedBy: string | null;
  cardAddedByFormer: boolean;
}

const FIELDS = `
  accountId ownerUserId ownerEmail billingEmail effectiveBillingEmail
  isOwner canManage billingEmailNotOnSite cardAddedBy cardAddedByFormer
`;

export const GET_BILLING_CONTACT = gql`
  query BillingContact($accountId: ID!) {
    billingContact(accountId: $accountId) { ${FIELDS} }
  }
`;

export const SET_BILLING_EMAIL = gql`
  mutation SetBillingEmail($accountId: ID!, $email: String) {
    setBillingEmail(accountId: $accountId, email: $email) { ${FIELDS} }
  }
`;

export const TRANSFER_SITE_OWNERSHIP = gql`
  mutation TransferSiteOwnership($accountId: ID!, $userId: ID!, $moveBillingEmail: Boolean) {
    transferSiteOwnership(accountId: $accountId, userId: $userId, moveBillingEmail: $moveBillingEmail) {
      ${FIELDS}
    }
  }
`;

export function useBillingContact(accountId: string | null | undefined) {
  const { data, loading, refetch } = useQuery<{ billingContact: BillingContact }>(
    GET_BILLING_CONTACT,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  return { contact: data?.billingContact ?? null, loading, refetch };
}

/** "Only the site owner, hai@blueline.io, can change the plan, card or billing email." */
export function ownerOnlyNote(c: BillingContact | null) {
  if (!c || c.canManage) return null;
  return c.ownerEmail
    ? `Only the site owner, ${c.ownerEmail}, can change the plan, card or billing email.`
    : 'Only an admin can change the plan, card or billing email.';
}

/** Where billing mail goes, and why: "hai@blueline.io · the owner’s email". */
export function billingEmailLine(c: BillingContact) {
  const email = c.effectiveBillingEmail || '—';
  if (!c.billingEmail) return { email, why: 'the owner’s email' };
  return { email, why: null };
}

/** The warnings Plan & billing shows, in the words the owner reads. */
export function billingWarnings(c: BillingContact | null): string[] {
  if (!c) return [];
  return [
    c.billingEmailNotOnSite
      ? `Billing emails go to ${c.effectiveBillingEmail}, who isn’t on this site. Change it so receipts and payment problems reach someone here.`
      : null,
    c.cardAddedByFormer && c.cardAddedBy
      ? `The card on file was added by ${c.cardAddedBy}, who’s no longer on this site. Add a card of your own and make it the default.`
      : null,
  ].filter((w): w is string => Boolean(w));
}

/**
 * Handing over ownership: whether billing mail followed the old owner, so the
 * dialog offers to send it to the new owner (a shared inbox stays put).
 */
export function billingFollowsOwner(c: BillingContact | null) {
  if (!c) return false;
  return (
    !c.billingEmail ||
    (Boolean(c.ownerEmail) && c.billingEmail.toLowerCase() === c.ownerEmail!.toLowerCase())
  );
}
